import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    Subsets,
    subsetSetter,
    subsetGetter,
    subsetUpdateSetter,
    SubsetGetterProps,
    subsetTotalGetter,
    SubsetTotalGetterProps,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";

export type Lag = {
    id: string;
    namn: string;
    idrott: string;
    idrott_display?: { id: string; namn: string };
    division?: string;
    plats?: string;
    plats_display?: { id: string; namn: string };
};

type State = BaseState & {
    lag: Map<Lag["id"], Lag>;
};

const getDefaultState: () => State = () => ({
    lag: new Map(),
    ...cloneDeep(baseState),
});

const moduleName = "lag";
const baseUrl = "skreg/lag/";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        lag: (state: State) => [...state.lag.values()],
        lagById:
            (state: State) =>
            ({ id }: { id?: string }) => {
                if (!id) return null;
                return state.lag.get(id);
            },
        lagSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({ ...props, state, collectionName: "lag" }),
        // Antalet rader som finns för subsetet, det värde paginatorn räknar på
        lagSubsetTotal: (state: State) => (props: SubsetTotalGetterProps<State>) =>
            subsetTotalGetter<State>({ ...props, state }),
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addLag: (
            state: State,
            {
                offset,
                subsetName = "default",
                lag = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                lag: Lag[];
            }
        ) =>
            subsetSetter<State, Lag>({
                offset,
                subsetName,
                state,
                collectionName: "lag",
                items: lag,
                includeDefaultSubset: true,
            }),
        updateLag: (
            state: State,
            {
                lag,
                offset,
            }: {
                lag: Lag[];
                offset: number;
            }
        ) =>
            subsetUpdateSetter<State, Lag>({
                offset,
                state,
                collectionName: "lag",
                items: lag,
                includeDefaultSubset: true,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        fetchLag: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams = {},
                forceRefresh = false,
            }: {
                limit?: number;
                page?: number;
                filterParams?: FilterParams;
                forceRefresh?: boolean;
            } = {}
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Lag>({
                    filterParams,
                    state,
                    page,
                    limit,
                    forceRefresh,
                    collectionName: "lag",
                    apiUrl: baseUrl,
                });

                if (results) {
                    commit("addLag", { subsetName, offset, lag: results });
                }
                if (typeof total === "number") {
                    commit("setTotal", { total, subsetName });
                }

                return { subsetName, total, nextPage };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        createLag: async ({ commit, state }: BaseActionsProps<State>, { lag }: { lag: Partial<Lag> }) => {
            try {
                const response = await klinikenApi.post(baseUrl, lag, getConfig({}));

                commit("addLag", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    lag: [response.data],
                });
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        updateLag: async ({ commit, state }: BaseActionsProps<State>, { lag }: { lag: Lag }) => {
            try {
                const response = await klinikenApi.put(`${baseUrl}${lag.id}/`, lag, getConfig({}));

                commit("updateLag", {
                    lag: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    },
};
